import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, inr } from "@/lib/api";
import { PageHeader } from "@/components/Bits";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast, Toaster } from "sonner";

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function AgedReceivables() {
    const [asOf, setAsOf] = useState(todayIso());
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setBusy(true);
        try {
            const { data } = await api.get("/reports/aged-receivables", { params: { as_of: asOf }});
            setData(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };
    useEffect(() => { load(); }, [asOf]);

    const totals = data?.totals || {};
    const rows = data?.rows || [];

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader
                title="Aged receivables"
                sub="Every customer with an outstanding balance, bucketed by how long the money has been owed. FIFO: the oldest unpaid debit sits in the oldest bucket."
                right={
                    <div className="flex items-end gap-2" data-testid="ar-controls">
                        <div>
                            <Label className="text-[11px]">As of</Label>
                            <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} data-testid="ar-as-of" className="h-9" />
                        </div>
                    </div>
                }
            />

            <div className="p-8 space-y-6">
                <div className="grid grid-cols-6 gap-4" data-testid="ar-totals">
                    <TotalCard label="Current (≤30d)" value={totals.current} tone="ok" testid="tot-current" />
                    <TotalCard label="31 – 60 days" value={totals.d30} tone="warn" testid="tot-d30" />
                    <TotalCard label="61 – 90 days" value={totals.d60} tone="warn" testid="tot-d60" />
                    <TotalCard label="91 – 120 days" value={totals.d90} tone="danger" testid="tot-d90" />
                    <TotalCard label="Over 120 days" value={totals.d90p} tone="danger" testid="tot-d90p" />
                    <TotalCard label="Total outstanding" value={totals.total} tone="dark" bold testid="tot-total" />
                </div>

                <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                    <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--se-border)" }}>
                        <div className="font-serif text-lg">Customers with an open balance</div>
                        <div className="text-xs text-stone-500">{rows.length} customer{rows.length === 1 ? "" : "s"}</div>
                    </div>
                    <table className="se-table" data-testid="ar-table">
                        <thead>
                            <tr>
                                <th>Customer</th>
                                <th>GSTIN</th>
                                <th>Oldest open</th>
                                <th className="text-right">Current</th>
                                <th className="text-right">31-60</th>
                                <th className="text-right">61-90</th>
                                <th className="text-right">91-120</th>
                                <th className="text-right">120+</th>
                                <th className="text-right">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {busy && !rows.length ? (
                                <tr><td colSpan="9" className="py-8 text-center text-stone-500">Loading…</td></tr>
                            ) : rows.length === 0 ? (
                                <tr><td colSpan="9" className="py-8 text-center text-stone-500">No outstanding receivables as of {asOf}. 🎉</td></tr>
                            ) : rows.map((r) => (
                                <tr key={r.customer_ledger_id} data-testid={`ar-row-${r.customer_ledger_id}`}>
                                    <td>
                                        <Link to={`/ledgers/${r.customer_ledger_id}`} className="hover:underline" style={{ color: "var(--se-brand)" }}>
                                            {r.customer_name}
                                        </Link>
                                        <div className="text-[10px] text-stone-400">{r.group_name}</div>
                                    </td>
                                    <td className="text-xs font-mono text-stone-500">{r.gstin || "—"}</td>
                                    <td className="text-xs font-mono">{r.oldest_open_date || "—"}</td>
                                    <AgeCell amount={r.current} tone="ok" />
                                    <AgeCell amount={r.d30} tone="warn" />
                                    <AgeCell amount={r.d60} tone="warn" />
                                    <AgeCell amount={r.d90} tone="danger" />
                                    <AgeCell amount={r.d90p} tone="danger" />
                                    <td className="se-num font-medium">₹ {inr(r.total)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
}

function TotalCard({ label, value, tone, bold = false, testid }) {
    const bg = tone === "danger" ? "rgba(225, 29, 72, 0.05)"
             : tone === "warn" ? "rgba(202, 138, 4, 0.05)"
             : tone === "ok" ? "rgba(5, 150, 105, 0.05)"
             : "#fff";
    const color = tone === "danger" ? "#9f1239"
                : tone === "warn" ? "#854d0e"
                : tone === "ok" ? "#065f46"
                : "#0f172a";
    return (
        <div className="border rounded-md px-4 py-3" style={{ borderColor: "var(--se-border)", background: bg }} data-testid={testid}>
            <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
            <div className={`se-num text-lg mt-1 ${bold ? "font-medium" : ""}`} style={{ color }}>
                ₹ {inr(value || 0)}
            </div>
        </div>
    );
}

function AgeCell({ amount, tone }) {
    const color = amount > 0 && tone === "danger" ? "#9f1239"
                : amount > 0 && tone === "warn" ? "#854d0e"
                : amount > 0 && tone === "ok" ? "#065f46"
                : "#a8a29e";
    return <td className="se-num" style={{ color }}>{amount > 0 ? `₹ ${inr(amount)}` : "—"}</td>;
}
